import { generateBreadcrumbScheme } from './breadcrumbs';
import { generateOfferCatalog } from './offerCatalog';
import { organization } from './organization';
import { generateReview } from './review';
import { rootUrl as baseUrl } from './shared';
import { generateWebPageSchema } from './webpage';
import { website } from './website';

export const generateLocalBusinessSchema = ({
  site,
  recommendations,
  breadcrumbsData,
  path,
  entityUrl
}) => {
  const { primary_location, primary_category, abn, rating } = site;
  const hasCategory = !!primary_category && !!primary_category.seo_key;

  if (hasCategory !== true) {
    return {}; // TODO: need seo solution for no primary category case;
  }

  const review = generateReview(recommendations);
  const hasOfferCatalog = generateOfferCatalog(site);

  const generateABN = abn =>
    abn
      ? {
          additionalProperty: {
            '@type': 'PropertyValue',
            name: 'ABN',
            value: abn,
            sameAs: `http://abr.business.gov.au/SearchByAbn.aspx?SearchText=${abn}`,
            description: `Current registration details for Australian Business Number ${abn}.`
          }
        }
      : {};
  const generateAddressServiceArea = primary_location => {
    const hasLocation =
      !!primary_location &&
      !!primary_location.street_address_name &&
      !!primary_location.street_address_number &&
      !!primary_location.suburb_seo_key &&
      !!primary_location.state_code;
    if (hasLocation !== true) {
      return {};
    }

    const {
      street_address_number,
      street_address_name,
      suburb_seo_key,
      state_code
    } = primary_location;

    const suburb = suburb_seo_key;
    const state = state_code;

    return {
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${street_address_number}, ${street_address_name}`,
        addressLocality: suburb,
        addressRegion: state
      },
      areaServed: [
        {
          '@type': 'State',
          name: state
        },
        {
          '@type': 'City',
          name: suburb
        }
      ]
    };
  };

  const generateRating = rating => {
    if (!!rating !== true) {
      return {};
    }

    const { ratings, star_rating } = rating;

    if (star_rating < 3) {
      return {};
    }

    return {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: star_rating,
        bestRating: 5,
        worstRating: 1,
        ratingCount: ratings
      }
    };
  };

  const generateEntity = ({ entityUrl }) => {
    if (entityUrl.length === 0) {
      return {};
    }
    return {
      mainEntityOfPage: {
        '@id': `${baseUrl}${entityUrl}#webpage`.toLowerCase()
      }
    };
  };

  const getBusinessSchemaType = category => {
    const hasCategory = !!category && !!category.seo_key;
    const categoryKey = hasCategory ? category.seo_key.toLowerCase() : '';
    switch (categoryKey) {
      case 'electricians':
        return 'Electrician';
      case 'locksmiths':
        return 'Locksmith';
      case 'painters_interior':
        return 'HousePainter';
      case 'painters_exterior':
        return 'HousePainter';
      case 'furniture_removal':
        return 'MovingCompany';
      case 'plumbers':
        return 'Plumber';
      case 'roofing_roofing_contractors':
        return 'RoofingContractor';
      case 'roofing':
        return 'RoofingContractor';
      default:
        return 'HomeAndConstructionBusiness';
    }
  };

  const { key, thumbnail } = site;
  const siteUrl = `${baseUrl}/connect/${key}`.toLocaleLowerCase();
  const hasBreadcrumbs = breadcrumbsData.length > 0;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      website,
      generateWebPageSchema({ path, hasBreadcrumbs }),
      generateBreadcrumbScheme(breadcrumbsData, path),
      {
        '@context': 'http://schema.org/',
        '@type': getBusinessSchemaType(primary_category),
        '@id': `${siteUrl}#tradie`,
        name: site.name,
        url: `${siteUrl}`,
        image: {
          '@type': 'ImageObject',
          url: thumbnail
        },
        ...generateABN(abn),
        ...generateAddressServiceArea(primary_location),
        ...generateRating(rating),
        review,
        ...generateEntity({ entityUrl }),
        hasOfferCatalog
      }
    ]
  };
};
